import React from "react";
import { useTranslation } from "react-i18next";
import { Map, ChevronRight } from "lucide-react";
import Button from "../common/Button";
import Modal from "../common/Modal";
import { color, radius } from "../../styles/theme";

/**
 * Web equivalent of the mobile app's EventAreaGuideModal
 * (src/Screens/add-location/components/EventAreaGuideModal.js).
 * Mobile plays a Lottie animation there; we don't have that asset for web,
 * so this uses a small static illustration instead — same explainer copy.
 */
const EventAreaGuideModal = ({ onClose }) => {
  const { t } = useTranslation("events");
  return (
    // e.stopPropagation not needed here anymore — Modal already stops the
    // inner click from bubbling to its own overlay; this modal is rendered
    // nested inside AddLocationModal's overlay div, so Modal's own onClose
    // (bound to its overlay) is what closes just this layer.
    <Modal title={t("eventAreaGuideModal.title")} onClose={onClose} maxWidth={400} footer={<Button onClick={onClose}>{t("eventAreaGuideModal.gotIt")}</Button>}>
      <div style={s.illustrationFrame}>
        <GuideIllustration />
      </div>

      <p style={s.subtitle}>{t("eventAreaGuideModal.subtitle")}</p>
      <p style={s.body}>{t("eventAreaGuideModal.body")}</p>
    </Modal>
  );
};

/** Simple pin + geofence boundary + check-in pins illustration. */
const GuideIllustration = () => {
  const { t } = useTranslation("events");
  return (
  <svg viewBox="0 0 220 140" width="100%" height="140" role="img" aria-label={t("eventAreaGuideModal.diagramAriaLabel")}>
    <polygon
      points="40,100 60,45 130,30 190,55 170,110 90,120"
      fill="rgba(108,99,255,0.14)"
      stroke="#6c63ff"
      strokeWidth="2"
      strokeDasharray="6 4"
    />
    {/* main event pin */}
    <circle cx="110" cy="75" r="9" fill="#6c63ff" stroke="#fff" strokeWidth="2" />
    <circle cx="110" cy="75" r="3" fill="#fff" />
    {/* check-in pins */}
    <circle cx="60" cy="45" r="5" fill="#f59e0b" stroke="#fff" strokeWidth="1.5" />
    <circle cx="170" cy="110" r="5" fill="#f59e0b" stroke="#fff" strokeWidth="1.5" />
    <circle cx="90" cy="120" r="5" fill="#f59e0b" stroke="#fff" strokeWidth="1.5" />
  </svg>
  );
};

/** Compact preview card, matching the mobile app's EventAreaGuideCard. */
export const EventAreaGuidePreviewCard = ({ onPress }) => {
  const { t } = useTranslation("events");
  return (
    <button type="button" className="row-link" onClick={onPress} style={s.previewCard}>
      <span style={s.previewIconBadge}><Map size={16} strokeWidth={2} /></span>
      <span style={s.previewTextWrap}>
        <span style={s.previewTitle}>{t("eventAreaGuideModal.previewTitle")}</span>
        <span style={s.previewBody}>{t("eventAreaGuideModal.previewBody")}</span>
      </span>
      <ChevronRight size={16} color={color.gray400} style={{ flexShrink: 0 }} />
    </button>
  );
};

const s = {
  illustrationFrame: {
    background: color.primary50, borderRadius: radius.md, padding: "12px 8px", marginBottom: 14,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  subtitle: { fontSize: 13, fontWeight: 600, color: color.textPrimary, margin: "0 0 6px" },
  body: { fontSize: 12.5, color: color.textMuted, margin: "0 0 4px", lineHeight: 1.5 },
  previewCard: {
    display: "flex", alignItems: "center", gap: 10, width: "100%",
    padding: "10px 12px", borderRadius: radius.sm, border: `1.5px solid ${color.border}`,
    background: color.gray50, cursor: "pointer", textAlign: "left", marginTop: 10,
  },
  previewIconBadge: {
    width: 30, height: 30, borderRadius: radius.sm, background: color.primary50, color: color.primary600,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  previewTextWrap: { flex: 1, display: "flex", flexDirection: "column", gap: 2 },
  previewTitle: { fontSize: 12, fontWeight: 700, color: color.textPrimary },
  previewBody: { fontSize: 11, color: color.textMuted },
};

export default EventAreaGuideModal;
